import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import LessonCard from "@/components/LessonCard";
import WalletDisplay from "@/components/WalletDisplay";
import { useWallet } from "@/hooks/useWallet";
import { useAssessments } from "@/hooks/useAssessments";
import AssessmentCard from "@/components/assessments/AssessmentCard";
import { ArrowLeft, TrendingUp, Lock, ChevronRight, Home, Building2, Briefcase, Car, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { isCourseAccessible, getCoursePhaseConfig } from "@/lib/curriculumPhases";
import LockedCourseModal from "@/components/curriculum/LockedCourseModal";
import { useAccessTier } from "@/hooks/useAccessTier";
import { useReleasedLessonIds } from "@/hooks/useLessonReleaseGate";
import type { AnyValue } from "@/lib/rowTypes";

interface Lesson {
  lesson_id: string;
  lesson_name: string;
  course_name: string;
  tier: string;
  lesson_order: number;
}

const CourseLessons = () => {
  const { courseName } = useParams<{ courseName: string }>();
  const { user } = useAuth();
  const { balance } = useWallet();
  const { getAssessmentsForCourse, getUserAttempts } = useAssessments();
  const navigate = useNavigate();
  const { tier, loading: tierLoading } = useAccessTier();

  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [completedIds, setCompletedIds] = useState<Set<string>>(new Set());
  const [assessments, setAssessments] = useState<Record<string, AnyValue>[]>([]);
  const [passedAssessments, setPassedAssessments] = useState<Set<string>>(new Set());

  const decoded = courseName ? decodeURIComponent(courseName) : "";
  // Only lessons that pass the canonical production-experience contract may be
  // opened by a customer. Everything else stays visible but locked as
  // "in development" — never deleted, never opened in a degraded state.
  const { readyIds: releasedLessonIds } = useReleasedLessonIds(lessons.map((l) => l.lesson_id));

  // Guard: redirect locked courses back with modal info
  const accessible = isCourseAccessible(decoded);

  useEffect(() => {
    if (!accessible && decoded) {
      // Redirect to curriculum map — course is locked
      toast.info("This learning path is part of an upcoming phase.", {
        description: "Continue with Financial Foundations while new courses are prepared.",
      });
      navigate("/curriculum", { replace: true });
    }
  }, [accessible, decoded, navigate]);

  // Paid product: no restricted customer tier. AccessGate handles entitlement.


  const isInvestingFundamentals = decoded === "Investing Fundamentals";
  const isRealEstate = decoded === "Real Estate (Homeownership)";
  const isREInvesting = decoded === "Real Estate Investing";
  const isEntrepreneurship = decoded === "Business & Startup Expansion";
  const isVehicle = decoded === "Transportation & Vehicle Ownership";

  useEffect(() => {
    const load = async () => {
      if (!courseName || !accessible) return;
      const { data: lessonsData } = await supabase.from("lessons").select("*").eq("course_name", decoded).order("lesson_order");
      setLessons(lessonsData || []);

      if (user && lessonsData && lessonsData.length > 0) {
        const lessonIds = lessonsData.map((lesson) => lesson.lesson_id);
        const { data: progress } = await supabase
          .from("user_progress")
          .select("lesson_id")
          .eq("user_id", user.id)
          .eq("completed", true)
          .in("lesson_id", lessonIds);
        setCompletedIds(new Set((progress || []).map((item) => item.lesson_id)));
      }

      // Load assessments for this course
      const courseAssessments = await getAssessmentsForCourse(decoded);
      setAssessments(courseAssessments);

      // Check which assessments user has passed
      if (user && courseAssessments.length > 0) {
        const passed = new Set<string>();
        for (const a of courseAssessments) {
          const attempts = await getUserAttempts(a.id);
          if (attempts.some((att) => att.passed)) passed.add(a.id);
        }
        setPassedAssessments(passed);
      }
    };

    void load();
  }, [courseName, decoded, user, accessible, getAssessmentsForCourse, getUserAttempts]);

  // Don't render if not accessible (will redirect)
  if (!accessible) return null;
  if (tierLoading) return null;

  const getLessonStatus = (lesson: Lesson, index: number): "locked" | "available" | "completed" => {
    if (completedIds.has(lesson.lesson_id)) return "completed";
    if (tier !== "admin" && !releasedLessonIds.has(lesson.lesson_id)) return "locked";
    if (index === 0) return "available";
    const prevLesson = lessons[index - 1];
    if (prevLesson && completedIds.has(prevLesson.lesson_id)) return "available";
    return "locked";
  };

  const handleLessonClick = (lesson: Lesson, index: number) => {
    const status = getLessonStatus(lesson, index);
    if (status === "locked") {
      const unreleased = tier !== "admin" && !releasedLessonIds.has(lesson.lesson_id);
      toast.info(
        unreleased
          ? "This lesson is still in development."
          : "Complete the previous lesson first to unlock this lesson.",
      );
      return;
    }
    navigate(`/lesson/${lesson.lesson_id}`);
  };

  const allLessonsCompleted = lessons.length > 0 && lessons.every((lesson) => completedIds.has(lesson.lesson_id));

  // Hard-lock: render nothing for beta-only users (redirect handled in effect above).
  if (tierLoading) return null;

  const renderModuleLink = (route: string, icon: AnyValue, name: string, unlockedDesc: string) => {
    const Icon = icon;
    return (
      <>
        <div className="flex items-center gap-3 pb-1 pt-4">
          <div className="h-px flex-1 bg-border" />
          <span className="font-heading text-xs uppercase tracking-wider text-muted-foreground">Module</span>
          <div className="h-px flex-1 bg-border" />
        </div>
        <button
          onClick={allLessonsCompleted ? () => navigate(route) : undefined}
          disabled={!allLessonsCompleted}
          className={`flex w-full items-center gap-4 rounded-2xl border p-5 text-left transition-all duration-200 ${
            allLessonsCompleted
              ? "glass-card glow-border cursor-pointer hover:shadow-card-hover"
              : "cursor-not-allowed border-border bg-muted/50 opacity-60"
          }`}
        >
          <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${allLessonsCompleted ? "bg-accent/10" : "bg-muted"}`}>
            {allLessonsCompleted ? <Icon className="h-5 w-5 text-accent" /> : <Lock className="h-5 w-5 text-muted-foreground" />}
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-heading text-sm font-semibold text-foreground">{name}</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {allLessonsCompleted ? unlockedDesc : `Complete all ${lessons.length} lessons to unlock`}
            </p>
          </div>
          {allLessonsCompleted && <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />}
        </button>
      </>
    );
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-background">
      <header className="sticky top-0 z-50 border-b border-border bg-card/80 backdrop-blur-md">
        <div className="container mx-auto flex max-w-5xl items-center justify-between gap-3 px-4 py-4">
          <div className="flex min-w-0 items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")} className="shrink-0 text-muted-foreground">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="truncate font-heading text-base font-bold text-foreground sm:text-lg">{decoded}</h1>
          </div>
          <div className="shrink-0">
            <WalletDisplay balance={balance} />
          </div>
        </div>
      </header>

      <main className="container mx-auto max-w-5xl px-4 py-6 sm:py-8">
        <div className="space-y-3">
          <button
            onClick={() => navigate("/financial-vocabulary")}
            className="flex w-full items-start gap-3 rounded-2xl border border-border/60 bg-card/60 p-4 text-left transition-colors hover:border-primary/30 hover:bg-primary/5"
          >
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-primary/20 bg-primary/10">
              <BookOpen className="h-5 w-5 text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-heading text-sm font-semibold text-foreground">Need help with financial terms?</p>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                Open the Key Terms mini-lesson for quick definitions of APR, ROI, assets, liabilities, cash flow, and more.
              </p>
            </div>
            <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
          </button>

          {lessons.map((lesson, index) => {
            const afterOrder = lesson.lesson_order;
            const midpointHere = assessments.filter(
              (a) => (a).is_midpoint_checkpoint && (a).trigger_after_lesson_order === afterOrder
            );
            const lessonsUpTo = lessons.filter((l) => l.lesson_order <= afterOrder);
            const midpointUnlocked = lessonsUpTo.every((l) => completedIds.has(l.lesson_id));

            return (
              <div key={lesson.lesson_id} className="space-y-3">
                <LessonCard
                  title={lesson.lesson_name}
                  status={getLessonStatus(lesson, index)}
                  lessonNumber={index + 1}
                  onClick={() => handleLessonClick(lesson, index)}
                />
                {midpointHere.map((a: AnyValue) => (
                  <AssessmentCard
                    key={a.id}
                    title={a.title}
                    type="midpoint"
                    badge={a.mastery_badge_award}
                    passed={passedAssessments.has(a.id)}
                    locked={!midpointUnlocked}
                    onClick={() => navigate(`/assessment/${a.id}`)}
                  />
                ))}
              </div>
            );
          })}

          {/* Capstone assessment at the end */}
          {assessments
            .filter((a: AnyValue) => a.is_capstone)
            .map((a: AnyValue) => (
              <AssessmentCard
                key={a.id}
                title={a.title}
                type="capstone"
                badge={a.mastery_badge_award}
                passed={passedAssessments.has(a.id)}
                locked={!allLessonsCompleted}
                onClick={() => navigate(`/assessment/${a.id}`)}
              />
            ))}

          {isInvestingFundamentals && lessons.length > 0 && renderModuleLink("/simulator", TrendingUp, "Market Simulator", "Apply what you learned — trade in a simulated market")}
          {isRealEstate && lessons.length > 0 && renderModuleLink("/homebuying", Home, "Homeownership Simulator", "Walk through buying a home — from down payment to closing")}
          {isREInvesting && lessons.length > 0 && renderModuleLink("/re-investing", Building2, "Real Estate Investing Simulator", "Buy properties, manage tenants, and build a portfolio")}
          {isEntrepreneurship && lessons.length > 0 && renderModuleLink("/entrepreneurship", Briefcase, "Entrepreneurship Module", "Launch and run your own business")}
          {isVehicle && renderModuleLink("/vehicle-ownership", Car, "Vehicle Ownership Module", "Walk through buying a vehicle — from needs to negotiation")}
        </div>
      </main>
    </div>
  );
};

export default CourseLessons;
